import { Injectable } from '@angular/core';
import { Observable, of, delay } from 'rxjs';
import { RoleSkillSetupService } from './role-skill-setup.service';
import { RoleSkillSetup, RoleSkillSetupParams } from '../../interfaces/career-setup/role-skill-setup.interface';
import { CommonSearchParam } from '../../interfaces/common-search-param.interface';
import { PageResponse } from '../../interfaces/page-response.interface';

@Injectable({
  providedIn: 'root'
})
export class RoleSkillSetupMockService extends RoleSkillSetupService {
  private mockData: RoleSkillSetup[] = [
    { 
      uniqueId: 'skill-grp-1', 
      roleUniqueId: 'role-sde', 
      roleName: 'Software Developer', 
      skills: ['Java', 'Spring Boot', 'Microservices'], 
      active: true 
    } as RoleSkillSetup,
    { 
      uniqueId: 'skill-grp-2', 
      roleUniqueId: 'role-frontend', 
      roleName: 'Frontend Developer', 
      skills: ['Angular', 'TypeScript', 'SCSS'], 
      active: true 
    } as RoleSkillSetup,
    { 
      uniqueId: 'skill-grp-3', 
      roleUniqueId: 'role-qa', 
      roleName: 'QA Engineer', 
      skills: ['Selenium', 'JMeter', 'Cypress'], 
      active: true 
    } as RoleSkillSetup
  ];

  getRoleSkills(params?: RoleSkillSetupParams): Observable<PageResponse<RoleSkillSetup>> {
    const commonParams: CommonSearchParam = {
      page: params?.page || 0,
      size: params?.size || 10
    };
    return this.findAll(commonParams);
  }

  override findAll(params: CommonSearchParam): Observable<PageResponse<RoleSkillSetup>> {
    const start = (params.page || 0) * (params.size || 10);
    const end = start + (params.size || 10);
    const content = this.mockData.slice(start, end);
    
    return of({
      content,
      totalElements: this.mockData.length,
      totalPages: Math.ceil(this.mockData.length / (params.size || 10)),
      pageable: {
        pageNumber: params.page || 0,
        pageSize: params.size || 10
      }
    } as any).pipe(delay(500));
  }

  override find(id: string): Observable<RoleSkillSetup> {
    const item = this.mockData.find(d => d.uniqueId === id);
    if (!item) throw new Error('Not found');
    return of(item).pipe(delay(500));
  }

  override create(data: Partial<RoleSkillSetup>): Observable<RoleSkillSetup> {
    const newItem = { 
      ...data, 
      uniqueId: 'skill-grp-' + Math.random().toString(36).substring(7),
      active: data.active !== false
    } as RoleSkillSetup;
    this.mockData.push(newItem);
    return of(newItem).pipe(delay(500));
  }

  override update(id: string, data: Partial<RoleSkillSetup>): Observable<RoleSkillSetup> {
    const index = this.mockData.findIndex(d => d.uniqueId === id);
    if (index === -1) throw new Error('Not found');
    this.mockData[index] = { ...this.mockData[index], ...data };
    return of(this.mockData[index]).pipe(delay(500));
  }

  override updateStatus(id: string, isActive: boolean): Observable<RoleSkillSetup> {
    const index = this.mockData.findIndex(d => d.uniqueId === id);
    if (index === -1) throw new Error('Not found');
    this.mockData[index] = { ...this.mockData[index], active: isActive };
    return of(this.mockData[index]).pipe(delay(500));
  }

  override delete(id: string): Observable<void> {
    this.mockData = this.mockData.filter(d => d.uniqueId !== id);
    return of(undefined).pipe(delay(500));
  }
}
